import React, { useState, useEffect } from 'react';
import config from '../../config';
import {
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import axios from 'axios';
import { toast } from 'react-toastify';

export default function AddArea() {
  // Add Form States
  const [departmentname, setDepartmentName] = useState('');
  const [printername, setPrinterName] = useState('');
  const [mobile, setMobile] = useState('');
  // When checked, THIS department's printer is used to print bills.
  const [isBillPrinter, setIsBillPrinter] = useState(false);

  // List States
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);

  // Edit Modal States
  const [openEdit, setOpenEdit] = useState(false);
  const [editingDept, setEditingDept] = useState(null);

  const company = JSON.parse(sessionStorage.getItem('companyDetails'));
  const token = sessionStorage.getItem('token');
  const companyId = company?.id;

  // ================== FETCH DEPARTMENTS ==================
  const fetchDepartments = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${config.API_BASE_URL}/home/GetAllDepartment/${companyId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setDepartments(Array.isArray(res.data) ? res.data : []);
      console.log(res.data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load departments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDepartments();
  }, [companyId]);

  // ================== ADD DEPARTMENT ==================
  const saveArea = async () => {
    if (!departmentname || !printername || !mobile) {
      toast.error('All fields are required');
      return;
    }

    const formData = new FormData();
    formData.append('Department', departmentname);
    formData.append('Printername', printername);
    formData.append('Mobile', mobile);
    formData.append('Companyid', companyId);
    formData.append('IsBillPrinter', isBillPrinter ? 'true' : 'false');

    try {
      const res = await axios.post(
        `${config.API_BASE_URL}/home/AddDepartment`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (res.data?.success) {
        toast.success(res.data.message || 'Department added successfully!');
        setDepartmentName('');
        setPrinterName('');
        setMobile('');
        setIsBillPrinter(false);
        fetchDepartments(); // Refresh list
      } else {
        toast.error('Something went wrong');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to add department');
    }
  };

  // ================== EDIT HANDLERS ==================
  const handleEditClick = (dept) => {
    setEditingDept({ ...dept });
    setOpenEdit(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditingDept((prev) => ({ ...prev, [name]: value }));
  };

  const updateDepartment = async () => {
    if (
      !editingDept?.department ||
      !editingDept?.printername ||
      !editingDept?.mobile
    ) {
      toast.error('All fields are required');
      return;
    }

    try {
      await axios.post(
        `${config.API_BASE_URL}/home/UpdateDepartment`,
        editingDept,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      toast.success('Department updated successfully!');
      setOpenEdit(false);
      fetchDepartments(); // Refresh list
    } catch (err) {
      console.error(err);
      toast.error('Failed to update department');
    }
  };

  return (
    <Container maxWidth='lg' sx={{ mt: 8 }}>
      {/* Add Form */}
      <Card sx={{ borderRadius: 4, p: 3, boxShadow: 4, mb: 5 }}>
        <CardContent>
          <Typography
            variant='h5'
            align='center'
            sx={{ mb: 3, fontWeight: 700 }}>
            Add Department
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Box display='flex' flexDirection='column' gap={2}>
            <TextField
              label='Department Name'
              fullWidth
              value={departmentname}
              onChange={(e) => setDepartmentName(e.target.value)}
            />
            <TextField
              label='Printer Name'
              fullWidth
              value={printername}
              onChange={(e) => setPrinterName(e.target.value)}
            />
            <TextField
              label='Mobile Number'
              type='number'
              fullWidth
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={isBillPrinter}
                  onChange={(e) => setIsBillPrinter(e.target.checked)}
                />
              }
              label='Bill print iss department ke printer se nikle (Bill Printer)'
            />

            <Button
              variant='contained'
              size='large'
              onClick={saveArea}
              sx={{ mt: 2 }}>
              Save Department
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Departments List */}
      <Card sx={{ borderRadius: 4, p: 3, boxShadow: 4 }}>
        <CardContent>
          <Typography variant='h5' sx={{ mb: 3, fontWeight: 700 }}>
            Department List
          </Typography>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>ID</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Department</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Printer Name</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Bill Printer</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Mobile</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Status</strong>
                  </TableCell>
                  <TableCell align='center'>
                    <strong>Action</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align='center'>
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : departments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align='center'>
                      No departments found
                    </TableCell>
                  </TableRow>
                ) : (
                  departments.map((dept) => (
                    <TableRow key={dept.id}>
                      <TableCell>{dept.id}</TableCell>
                      <TableCell>{dept.department}</TableCell>
                      <TableCell>{dept.printername}</TableCell>
                      <TableCell>
                        {dept.isBillPrinter ? (
                          <Typography color='success.main' fontWeight={600}>
                            Bill ✓
                          </Typography>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>{dept.mobile}</TableCell>
                      <TableCell>
                        <Typography
                          color={dept.isactive ? 'success.main' : 'error.main'}
                          fontWeight={500}>
                          {dept.isactive ? 'Active' : 'Inactive'}
                        </Typography>
                      </TableCell>
                      <TableCell align='center'>
                        <IconButton
                          color='primary'
                          onClick={() => handleEditClick(dept)}>
                          <EditIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Edit Modal */}
      <Dialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        maxWidth='sm'
        fullWidth>
        <DialogTitle>Edit Department</DialogTitle>
        <DialogContent>
          <Box display='flex' flexDirection='column' gap={2} sx={{ mt: 2 }}>
            <TextField
              label='Department Name'
              fullWidth
              name='department'
              value={editingDept?.department || ''}
              onChange={handleEditChange}
            />
            <TextField
              label='Printer Name'
              fullWidth
              name='printername'
              value={editingDept?.printername || ''}
              onChange={handleEditChange}
            />
            <TextField
              label='Mobile Number'
              type='number'
              fullWidth
              name='mobile'
              value={editingDept?.mobile || ''}
              onChange={handleEditChange}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!editingDept?.isBillPrinter}
                  onChange={(e) =>
                    setEditingDept((prev) => ({
                      ...prev,
                      isBillPrinter: e.target.checked,
                    }))
                  }
                />
              }
              label='Bill print iss department ke printer se nikle (Bill Printer)'
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)} color='inherit'>
            Cancel
          </Button>
          <Button onClick={updateDepartment} variant='contained'>
            Update Department
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
